import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { projectFirestore } from "../firebase";
import {
  UNBATCH_FINISHED,
  UNBATCH_STARTED,
  countReturned,
  finishUnbatchRequest,
  isBatchGone,
  isUnbatchRequesting,
  linesHeldFromQueue,
  readUnbatches,
  writeUnbatches,
} from "./unbatchTracking";
import { settleUnbatch, settledMessage } from "./unbatchSettlement";
import { requestBatchDetails } from "../api/batches";
import { batchSavePhase, SAVING_STATUSES } from "./batchSaveProgress";
import {
  DTF_EXPORT_PROGRESS,
  DTF_FINISH_FOLDER,
  clearDtfExport,
  takeDtfExportAfterLoad,
} from "../pages/Nester/dtfExportProgress";
import { resolveHotFolder } from "../pages/Nester/nesterHotFolder";
import "./BatchSaveWatcher.scss";

export const BATCH_SAVES_KEY = "riverr.batchSaves";
export const BATCH_SAVE_STARTED = "riverr:batch-save-started";
export const BATCH_SAVE_FINISHED = "riverr:batch-save-finished";
const BATCH_SAVE_PENDING = "riverr:batch-save-pending";
const BATCH_SAVE_PENDING_END = "riverr:batch-save-pending-end";
const SLOW_MS = 10 * 60 * 1000;
const HIDE_OK_MS = 10 * 1000;

const readSaves = () => {
  try {
    const rows = JSON.parse(localStorage.getItem(BATCH_SAVES_KEY) || "[]");
    return Array.isArray(rows) ? rows : [];
  } catch (_) {
    return [];
  }
};

export const trackedBatchItemCount = (batchId) => {
  const row = readSaves().find((r) => r.batchId === String(batchId));
  return Number(row?.itemCount) || 0;
};

export const isBatchSaveTracked = (batchId) =>
  readSaves().some((row) => row.batchId === String(batchId));

const writeSaves = (rows) => {
  try {
    localStorage.setItem(BATCH_SAVES_KEY, JSON.stringify(rows));
  } catch (_) {
    /* ignore */
  }
};

/**
 * Call when the user clicks create, before createBatch returns (the server
 * reserves every order first, which can take several seconds). Returns a key
 * for trackBatchSave / endPendingBatchSave.
 */
export const startPendingBatchSave = (label) => {
  const key = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  window.dispatchEvent(
    new CustomEvent(BATCH_SAVE_PENDING, { detail: { key, label } })
  );
  return key;
};

export const endPendingBatchSave = (key) => {
  if (!key) return;
  window.dispatchEvent(
    new CustomEvent(BATCH_SAVE_PENDING_END, { detail: { key } })
  );
};

/** Call after createBatch returns; the tray follows the save to the end. */
export const trackBatchSave = (batch, pendingKey, itemCount) => {
  if (batch?.id && batch?.enterpriseId) {
    const rows = readSaves().filter((row) => row.batchId !== batch.id);
    rows.push({
      batchId: String(batch.id),
      batchNumber: batch.batchNumber || "",
      enterpriseId: String(batch.enterpriseId),
      startedAt: Date.now(),
      itemCount: Number(itemCount) || 0,
    });
    writeSaves(rows);
    window.dispatchEvent(new CustomEvent(BATCH_SAVE_STARTED));
  }
  endPendingBatchSave(pendingKey);
};

const PendingCard = ({ label }) => (
  <div className="batch-save-tray__card batch-save-tray__card--busy">
    <div className="batch-save-tray__head">
      <span className="batch-save-tray__spinner" aria-hidden />
      <strong>Starting batch</strong>
    </div>
    <div className="batch-save-tray__copy">
      {label ? `Reserving ${label} for this batch…` : "Reserving your orders for this batch…"}
    </div>
    <div className="batch-save-tray__track">
      <div className="batch-save-tray__fill" style={{ width: "6%" }} />
    </div>
  </div>
);

const MissingList = ({ missing }) => (
  <ul className="batch-save-tray__missing">
    {missing.map((row) => (
      <li key={`${row.orderId}-${row.reason}`}>{row.detail}</li>
    ))}
  </ul>
);

const SaveCard = ({ save, onDone, onDismiss }) => {
  const [snap, setSnap] = useState({ loading: true });
  const [serverStatus, setServerStatus] = useState("");
  const [now, setNow] = useState(Date.now());

  // The Firestore listener can miss a batch the server already finished.
  // Ask the same endpoint the batch page uses, and stop when it says done.
  useEffect(() => {
    if (!save.batchId) return undefined;
    if (serverStatus && !SAVING_STATUSES.has(serverStatus)) return undefined;
    let stopped = false;
    const pull = () => {
      requestBatchDetails(save.batchId)
        .then((res) => {
          if (stopped) return;
          const status = res?.data?.batch?.status || "";
          if (status) setServerStatus(status);
        })
        .catch(() => {});
    };
    pull();
    const timer = setInterval(pull, 3000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [save.batchId, serverStatus]);

  useEffect(() => {
    let stopped = false;
    let retryTimer = null;
    let unsubscribe = () => {};
    const listen = () => {
      unsubscribe();
      const ref = doc(
        projectFirestore,
        "enterprises",
        save.enterpriseId,
        "batches",
        save.batchId
      );
      unsubscribe = onSnapshot(
        ref,
        (next) => {
          if (stopped) return;
          setSnap(
            next.exists()
              ? { exists: true, data: next.data() }
              : { exists: false }
          );
        },
        () => {
          if (stopped) return;
          // A dropped listener is not a failed save. Keep the last record and
          // try again. Never unbatch from here; the server is still writing.
          setSnap((prev) => ({
            exists: Boolean(prev?.exists),
            data: prev?.data,
            error: true,
          }));
          retryTimer = setTimeout(listen, 5000);
        }
      );
    };
    listen();
    return () => {
      stopped = true;
      clearTimeout(retryTimer);
      unsubscribe();
    };
  }, [save.batchId, save.enterpriseId]);

  const missing = snap.data?.missingOrders || [];
  const phase = batchSavePhase({
    loading: snap.loading,
    exists: snap.exists,
    status: snap.data?.status,
    serverStatus,
    startedAt: save.startedAt,
    now,
  });
  const saving = phase === "saving";
  const unconfirmed = phase === "unconfirmed";
  const uncertain =
    saving && !snap.loading && !(snap.exists && SAVING_STATUSES.has(snap.data?.status));

  useEffect(() => {
    if (saving) return undefined;
    onDone(save.batchId, unconfirmed ? -1 : missing.length);
    if (unconfirmed || missing.length) return undefined;
    const timer = setTimeout(() => onDismiss(save.batchId), HIDE_OK_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saving, unconfirmed, missing.length]);

  useEffect(() => {
    if (!saving) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(timer);
  }, [saving]);

  const progress = snap.data?.progress || {};
  const total = progress.totalOrders || 0;
  const done = progress.savedOrders || 0;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const pieces = Number(save.itemCount) || Number(snap.data?.itemsCount) || 0;
  const pieceLabel = pieces
    ? `${pieces.toLocaleString()} item${pieces === 1 ? "" : "s"}`
    : "";
  const slow = saving && now - save.startedAt > SLOW_MS;
  const tone = unconfirmed || (!saving && missing.length) ? "error" : saving ? "busy" : "ok";
  const batchLabel = snap.data?.batchNumber || save.batchNumber || save.batchId;

  return (
    <div className={`batch-save-tray__card batch-save-tray__card--${tone}`}>
      <div className="batch-save-tray__head">
        {saving && <span className="batch-save-tray__spinner" aria-hidden />}
        <strong>
          {saving && uncertain && `Still saving batch ${batchLabel}`}
          {saving && !uncertain && `Saving batch ${batchLabel}`}
          {!saving && !unconfirmed && `Batch ${batchLabel} is ready`}
          {unconfirmed && `Couldn't confirm batch ${batchLabel}`}
        </strong>
        {!saving && (
          <button
            type="button"
            className="batch-save-tray__dismiss"
            aria-label="Dismiss"
            onClick={() => onDismiss(save.batchId)}
          >
            ×
          </button>
        )}
      </div>
      {saving && (
        <>
          <div className="batch-save-tray__copy">
            {uncertain
              ? "Still saving. This batch is finishing in the background."
              : pieceLabel
                ? `Saving ${pieceLabel}. You can keep working.`
                : "Saving this batch. You can keep working."}
            {slow && " This is taking longer than usual; it will keep retrying."}
          </div>
          <div className="batch-save-tray__track">
            <div
              className="batch-save-tray__fill"
              style={{ width: `${Math.max(6, pct)}%` }}
            />
          </div>
        </>
      )}
      {!saving && !unconfirmed && (
        <div className="batch-save-tray__copy">
          {pieceLabel ? `${pieceLabel}. ` : ""}
          <Link to={`/batches/${encodeURIComponent(batchLabel)}`}>Open batch</Link>
        </div>
      )}
      {unconfirmed && (
        <div className="batch-save-tray__copy">
          Couldn't confirm this save.{" "}
          <Link to={`/batches/${encodeURIComponent(batchLabel)}`}>Check the batch</Link>{" "}
          before unbatching.
        </div>
      )}
      {missing.length > 0 && (
        <>
          <div className="batch-save-tray__copy batch-save-tray__copy--error">
            {missing.length} order{missing.length === 1 ? "" : "s"} did not go
            on this batch:
          </div>
          <MissingList missing={missing} />
        </>
      )}
    </div>
  );
};

const UNBATCH_POLL_MS = 5000;
const UNBATCH_GONE_CHECK_MS = 5000;
const UNBATCH_POLL_LARGE_MS = 10000;
const UNBATCH_LARGE_ORDERS = 300;
// Items are rebuilt order by order after an unbatch. If the count stops moving
// this long, the rest are probably held back (cancelled, shipped, on hold).
const UNBATCH_STALL_MS = 4 * 60 * 1000;
const UNBATCH_MAX_ERRORS = 5;

const UnbatchCard = ({ job, onFinished, onDismiss }) => {
  const [progress, setProgress] = useState({ back: 0, total: 0, unit: "items" });
  const [phase, setPhase] = useState("checking");
  const [holdReason, setHoldReason] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [, setClock] = useState(0);
  const requesting = isUnbatchRequesting(job);

  useEffect(() => {
    if (!requesting) return undefined;
    // Re-check the give-up window without waiting for another storage event.
    const timer = setInterval(() => setClock((n) => n + 1), 30000);
    return () => clearInterval(timer);
  }, [requesting]);

  useEffect(() => {
    if (!requesting) return undefined;
    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        if ((await isBatchGone(job)) && !cancelled) {
          finishUnbatchRequest(job.batchId, true);
        }
      } catch (_) {
        /* keep waiting on the request */
      }
    }, UNBATCH_GONE_CHECK_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.batchId, requesting]);

  useEffect(() => {
    if (requesting) {
      setPhase("checking");
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    let lastBack = -1;
    let lastChangeAt = Date.now();
    let errors = 0;
    const interval =
      job.orderIds.length > UNBATCH_LARGE_ORDERS
        ? UNBATCH_POLL_LARGE_MS
        : UNBATCH_POLL_MS;
    setPhase("checking");

    const tick = async () => {
      try {
        const next = await countReturned(job);
        if (cancelled) return;
        errors = 0;
        setProgress(next);
        if (next.back !== lastBack) {
          lastBack = next.back;
          lastChangeAt = Date.now();
        }
        if (next.back >= next.total && next.total > 0) {
          setPhase("done");
          onFinished(job.batchId, 0);
          return;
        }
        const held = await linesHeldFromQueue(job);
        if (cancelled) return;
        const settlement = settleUnbatch({
          back: next.back,
          total: next.total,
          lines: held,
        });
        if (settlement.phase === "settled") {
          setHoldReason(settlement.reason);
          setPhase("settled");
          onFinished(job.batchId, settlement.held);
          return;
        }
        if (Date.now() - lastChangeAt > UNBATCH_STALL_MS) {
          setPhase("stalled");
          onFinished(job.batchId, next.total - next.back);
          return;
        }
      } catch (_) {
        if (cancelled) return;
        errors += 1;
        if (errors >= UNBATCH_MAX_ERRORS) {
          setPhase("error");
          return;
        }
      }
      timer = setTimeout(tick, interval);
    };
    tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.batchId, attempt, requesting]);

  useEffect(() => {
    if (phase !== "done" && phase !== "settled") return undefined;
    const timer = setTimeout(() => onDismiss(job.batchId), HIDE_OK_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const { back, total, unit } = progress;
  const pct = total ? Math.round((back / total) * 100) : 0;
  const n = (value) => Number(value || 0).toLocaleString();
  const apartLabel = job.itemCount
    ? `${n(job.itemCount)} item${Number(job.itemCount) === 1 ? "" : "s"}`
    : `${job.orderIds.length} order${job.orderIds.length === 1 ? "" : "s"}`;
  const busy = phase === "checking";
  const tone = phase === "done" || phase === "settled" ? "ok" : busy ? "busy" : "error";
  const batchLabel = job.batchNumber || job.batchId;
  const left = Math.max(0, total - back);

  return (
    <div className={`batch-save-tray__card batch-save-tray__card--${tone}`}>
      <div className="batch-save-tray__head">
        {busy && <span className="batch-save-tray__spinner" aria-hidden />}
        <strong>
          {busy && `Unbatching ${batchLabel}`}
          {(phase === "done" || phase === "settled") &&
            `Batch ${batchLabel} is unbatched`}
          {phase === "stalled" && `Batch ${batchLabel} is mostly unbatched`}
          {phase === "error" && `Could not check batch ${batchLabel}`}
        </strong>
        <button
          type="button"
          className="batch-save-tray__dismiss"
          aria-label="Dismiss"
          onClick={() => onDismiss(job.batchId)}
        >
          ×
        </button>
      </div>
      <div className="batch-save-tray__copy">
        {busy &&
          requesting &&
          `Taking apart ${apartLabel}… You can keep working.`}
        {busy &&
          !requesting &&
          (total
            ? `${n(back)} of ${n(total)} ${unit} back on the waiting list. You can keep working.`
            : "Sending items back to the waiting list…")}
        {phase === "done" && `All ${n(total)} ${unit} are back on the waiting list.`}
        {phase === "settled" &&
          settledMessage({ back, total, unit, reason: holdReason })}
        {phase === "stalled" &&
          `${n(back)} of ${n(total)} ${unit} are back. ${left} ${
            left === 1 ? "has" : "have"
          } not come back yet; the order may be cancelled, shipped, or on hold.`}
        {phase === "error" &&
          "The unbatch went through, but progress could not be checked."}
      </div>
      {(busy || phase === "stalled") && total > 0 && (
        <div className="batch-save-tray__track">
          <div
            className="batch-save-tray__fill"
            style={{ width: `${Math.max(6, pct)}%` }}
          />
        </div>
      )}
      {(phase === "stalled" || phase === "error") && (
        <div className="batch-save-tray__copy">
          <button
            type="button"
            className="batch-save-tray__link"
            onClick={() => setAttempt((n) => n + 1)}
          >
            Check again
          </button>
        </div>
      )}
    </div>
  );
};

/** App-level tray for batches saving or unbatching in the background. Survives navigation. */
const DtfExportCard = ({ job, onDismiss, onFinishFolder }) => {
  const tone = job.phase === "error" ? "error" : job.phase === "ok" ? "ok" : "busy";
  const showDismiss = tone !== "busy" || job.needsFolder;
  return (
    <div className={`batch-save-tray__card batch-save-tray__card--${tone}`}>
      <div className="batch-save-tray__head">
        {tone === "busy" && !job.needsFolder && (
          <span className="batch-save-tray__spinner" aria-hidden />
        )}
        <strong>{job.title || "Exporting sheets"}</strong>
        {showDismiss && (
          <button
            type="button"
            className="batch-save-tray__dismiss"
            aria-label="Dismiss"
            onClick={onDismiss}
          >
            ×
          </button>
        )}
      </div>
      {job.copy ? <div className="batch-save-tray__copy">{job.copy}</div> : null}
      {job.linkTo && job.linkLabel && tone === "ok" ? (
        <Link className="batch-save-tray__link" to={job.linkTo} onClick={onDismiss}>
          {job.linkLabel}
        </Link>
      ) : null}
      {job.needsFolder ? (
        <button
          type="button"
          className="batch-save-tray__action"
          onClick={onFinishFolder}
        >
          Choose printer folder
        </button>
      ) : null}
      {tone === "busy" && !job.needsFolder && (
        <div className="batch-save-tray__track">
          <div
            className="batch-save-tray__fill"
            style={{ width: `${Math.max(6, Number(job.pct) || 0)}%` }}
          />
        </div>
      )}
    </div>
  );
};

const BatchSaveWatcher = () => {
  const navigate = useNavigate();
  const resumeNavRef = useRef(false);
  const [saves, setSaves] = useState(readSaves);
  const [unbatches, setUnbatches] = useState(readUnbatches);
  const [pending, setPending] = useState([]);
  const [dtfExport, setDtfExport] = useState(takeDtfExportAfterLoad);

  useEffect(() => {
    const add = (e) => {
      const { key, label } = e.detail || {};
      if (key) setPending((rows) => [...rows, { key, label }]);
    };
    const end = (e) => {
      const key = e.detail?.key;
      setPending((rows) => rows.filter((row) => row.key !== key));
    };
    window.addEventListener(BATCH_SAVE_PENDING, add);
    window.addEventListener(BATCH_SAVE_PENDING_END, end);
    return () => {
      window.removeEventListener(BATCH_SAVE_PENDING, add);
      window.removeEventListener(BATCH_SAVE_PENDING_END, end);
    };
  }, []);

  useEffect(() => {
    const onExport = (event) => setDtfExport(event.detail || null);
    window.addEventListener(DTF_EXPORT_PROGRESS, onExport);
    return () => window.removeEventListener(DTF_EXPORT_PROGRESS, onExport);
  }, []);

  useEffect(() => {
    if (!dtfExport?.resume || !dtfExport.jobId || resumeNavRef.current) return;
    resumeNavRef.current = true;
    navigate("/dtf-station/gangsheet", {
      state: { resumeJobId: dtfExport.jobId },
    });
  }, [dtfExport, navigate]);

  useEffect(() => {
    if (dtfExport?.phase !== "ok") return undefined;
    const timer = setTimeout(() => {
      clearDtfExport();
      setDtfExport(null);
    }, HIDE_OK_MS);
    return () => clearTimeout(timer);
  }, [dtfExport]);

  useEffect(() => {
    const reload = () => setSaves(readSaves());
    const reloadUnbatches = () => setUnbatches(readUnbatches());
    window.addEventListener(BATCH_SAVE_STARTED, reload);
    window.addEventListener(UNBATCH_STARTED, reloadUnbatches);
    window.addEventListener("storage", reload);
    window.addEventListener("storage", reloadUnbatches);
    return () => {
      window.removeEventListener(BATCH_SAVE_STARTED, reload);
      window.removeEventListener(UNBATCH_STARTED, reloadUnbatches);
      window.removeEventListener("storage", reload);
      window.removeEventListener("storage", reloadUnbatches);
    };
  }, []);

  const onUnbatchFinished = (batchId, leftCount) => {
    window.dispatchEvent(
      new CustomEvent(UNBATCH_FINISHED, { detail: { batchId, leftCount } })
    );
  };

  const onUnbatchDismiss = (batchId) => {
    const rows = readUnbatches().filter((row) => row.batchId !== batchId);
    writeUnbatches(rows);
    setUnbatches(rows);
  };

  const onDone = (batchId, missingCount) => {
    window.dispatchEvent(
      new CustomEvent(BATCH_SAVE_FINISHED, {
        detail: { batchId, missingCount, saved: missingCount >= 0 },
      })
    );
  };

  const onDismiss = (batchId) => {
    const rows = readSaves().filter((row) => row.batchId !== batchId);
    writeSaves(rows);
    setSaves(rows);
  };

  if (!saves.length && !unbatches.length && !pending.length && !dtfExport) return null;
  return (
    <div className="batch-save-tray" role="status" aria-live="polite">
      {dtfExport ? (
        <DtfExportCard
          job={dtfExport}
          onDismiss={() => {
            clearDtfExport();
            setDtfExport(null);
          }}
          onFinishFolder={async () => {
            let hotFolder = null;
            try {
              hotFolder = await resolveHotFolder({ forcePick: true });
            } catch {
              hotFolder = null;
            }
            if (!hotFolder) return;
            const path = window.location.pathname || "";
            const onSheet =
              path.includes("gangsheet") ||
              path === "/nester" ||
              path.startsWith("/products/nester");
            if (onSheet) {
              window.dispatchEvent(
                new CustomEvent(DTF_FINISH_FOLDER, { detail: { hotFolder } })
              );
              return;
            }
            navigate("/dtf-station/gangsheet", {
              state: { resumeJobId: dtfExport.jobId },
            });
          }}
        />
      ) : null}
      {pending.map((row) => (
        <PendingCard key={row.key} label={row.label} />
      ))}
      {saves.map((save) => (
        <SaveCard
          key={save.batchId}
          save={save}
          onDone={onDone}
          onDismiss={onDismiss}
        />
      ))}
      {unbatches.map((job) => (
        <UnbatchCard
          key={`unbatch-${job.batchId}`}
          job={job}
          onFinished={onUnbatchFinished}
          onDismiss={onUnbatchDismiss}
        />
      ))}
    </div>
  );
};

export default BatchSaveWatcher;
